/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { PidxBannerService, PidxTimerLabelModule } from '@myprofile-ui/pidx-ui-components'
import { PidxScrollToService, PidxUtilsModule, WINDOW } from '@myprofile-ui/pidx-utils'
import { TranslateService } from '@ngx-translate/core'
import { PinCodeChangeEventDetail, PinCodeState, PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular'
import { lastValueFrom } from 'rxjs'
import { ConfirmationCodeStatus } from '../../enums/confirmation-code-status'
import { VerificationErrorCode } from '../../enums/verification-error-code'
import { MyProfileEmailWidgetVerificationTranslations } from '../../interfaces/myprofile-email-widget.interfaces'
import { EmailCommunicationDto } from '../../../../../../apps/myprofile-ui/src/app/services/email/email.service'
import { CommunicationVerificationService } from '../../../../../../apps/myprofile-ui/src/app/services/communication-verification/communicationVerification.service'

@Component({
  selector: 'myprofile-email-verification',
  templateUrl: './myprofile-email-verification.component.html',
  standalone: true,
  imports: [PorscheDesignSystemModule, PidxTimerLabelModule, PidxUtilsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MyProfileEmailVerificationComponent implements OnInit {
  private notificationsService = inject(PidxBannerService)
  private toastManager = inject(ToastManager)
  private communicationVerificationService = inject(CommunicationVerificationService)
  private translateService = inject(TranslateService)
  private pidxScrollToService = inject(PidxScrollToService)
  private element = inject(ElementRef)
  private window = inject<Window>(WINDOW)

  readonly email = input.required<EmailCommunicationDto>()
  readonly translations = input.required<MyProfileEmailWidgetVerificationTranslations>()
  readonly country = input.required<string>()
  readonly locale = input.required<string>()

  readonly cancelEvent = output()
  readonly verified = output()
  /** Emitted when the backend state requires the widget to refresh (e.g. e-mail already confirmed or removed). */
  readonly reloadEvent = output()

  readonly isDisabled = signal(false)
  /** Disables the resend-code button after the backend blocks further OTP requests. */
  readonly resendDisabled = signal(false)
  readonly confirmationCodeStatus = signal<ConfirmationCodeStatus>(ConfirmationCodeStatus.CODE_STATUS_DEFAULT)
  readonly messages = signal<string[]>([])
  readonly messageState = signal<PinCodeState>('none')
  readonly waitingTime = 30
  protected readonly ConfirmationCodeStatus = ConfirmationCodeStatus

  ngOnInit() {
    this.window.setTimeout(() => {
      this.window.document.getElementById('emailRequestAndInsertHeadline')?.focus()
    }, 0)
    this.requestConfirmationCode()
  }

  async requestConfirmationCode() {
    try {
      this.window.setTimeout(() => {
        this.pidxScrollToService.scrollToElement(this.element.nativeElement, 24)
      }, 0)
      await lastValueFrom(this.communicationVerificationService.sendEmailVerification(this.country(), this.locale(), this.email().emailId!))
      this.toastManager.addMessage({ text: this.translations().verificationCodeSentMessage, state: 'success' })
    } catch (error) {
      this.handleVerificationError(error as HttpErrorResponse)
    }
  }

  async validateConfirmationCode(e: CustomEvent<PinCodeChangeEventDetail>) {
    if (!e.detail.isComplete) return
    this.confirmationCodeStatus.set(ConfirmationCodeStatus.CODE_STATUS_VALIDATION_PENDING)
    this.messages.set([])

    const confirmationCode = e.detail.value

    try {
      await lastValueFrom(
        this.communicationVerificationService.verifyEmailWithOtpPin(this.country(), this.locale(), this.email().emailId!, {
          otpPin: confirmationCode,
        }),
      )
      this.messageState.set('success')
      this.verified.emit()
    } catch (error) {
      this.handleVerificationError(error as HttpErrorResponse)
    }
  }

  public cancel() {
    this.cancelEvent.emit()
  }

  /**
   * Maps the backend `errorCode` to the appropriate behaviour for the OTP step:
   * - expired-transaction codes show the inline "expired code" message,
   * - already-confirmed / missing-email codes reload the widget,
   * - a temporary validation block disables the inputs and shows an error toast,
   * - throttling codes show the inline "too many" messages.
   * Anything else falls back to the backend-provided key or an HTTP-status-based message.
   */
  private handleVerificationError(error: HttpErrorResponse) {
    switch (error?.error?.errorCode) {
      case VerificationErrorCode.RESOURCE_NOT_FOUND_VERIFICATION_TRANSACTION:
      case VerificationErrorCode.BUSINESS_CONSTRAINT_TRANSACTION_ABORTED:
        this.setInlineError(this.translations().errorExpiredCode)
        return
      case VerificationErrorCode.BUSINESS_CONSTRAINT_TRANSACTION_ALREADY_CONFIRMED:
      case VerificationErrorCode.RESOURCE_NOT_FOUND_EMAIL:
        this.reloadEvent.emit()
        return
      case VerificationErrorCode.LOCKED_EMAIL_OTP_VALIDATION_TEMPORARILY_BLOCKED:
        this.isDisabled.set(true)
        this.resendDisabled.set(true)
        this.setInlineError(this.translations().errorTooManyAttempts)
        return
      case VerificationErrorCode.TOO_MANY_REQUESTS_RECENT_COMM_VERIFICATION_PENDING:
        this.setInlineError(this.translations().errorTooManyRequests)
        return
      case VerificationErrorCode.TOO_MANY_REQUESTS_OTP_REQUESTING_BLOCKED:
        this.resendDisabled.set(true)
        this.setInlineError(this.translations().errorTooManyRequests)
        return
      default:
        this.setInlineError(this.resolveErrorMessage(error))
    }
  }

  private setInlineError(message: string) {
    this.confirmationCodeStatus.set(ConfirmationCodeStatus.CODE_STATUS_DEFAULT)
    this.messages.set([message])
    this.messageState.set('error')
  }

  /**
   * Pass the backend-provided translation key straight through (parity with the phone flow). When the backend does
   * not supply a key, fall back to an OTP-specific message chosen from the HTTP status code.
   */
  private resolveErrorMessage(error: HttpErrorResponse): string {
    const errorKey = error?.error?.collection?.error?.message
    if (errorKey && typeof errorKey === 'string') {
      return this.translateService.instant(errorKey)
    }

    switch (error?.status) {
      case 429:
        return this.translations().errorTooManyRequests
      case 410:
        return this.translations().errorExpiredCode
      default:
        return this.translations().errorInvalidCode
    }
  }
}
